import {
  createContext,
  FC,
  PropsWithChildren,
  SetStateAction,
  useMemo,
  useState,
  Dispatch,
} from 'react';
import {
  Associate,
  AssociateFilter,
  useGetAssociatesForTransferFilters,
  useGetFilteredAssociatesForTransfer,
} from '@vs-online-only-app/state';

type TransferState = {
  isLoading: boolean;
  filteredAssociates: Associate[];
  error: string;
  setSelectedDepartmentIds: Dispatch<SetStateAction<string[] | undefined>>;
  selectedDepartmentIds?: string[];
  associateFilters: AssociateFilter[];
};

const TransferContext = createContext<TransferState>({} as TransferState);

const TransferProvider: FC<PropsWithChildren> = (props) => {
  // Initial department ids are set in a useEffect in the AssociateFilters component
  // based on which departments have online associates
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<
    string[] | undefined
  >();

  const {
    data: associateFilters,
    isLoading: filtersLoading,
    error: filtersError,
  } = useGetAssociatesForTransferFilters();

  const {
    data: filteredAssociates,
    isLoading: associatesLoading,
    error: associatesError,
  } = useGetFilteredAssociatesForTransfer(selectedDepartmentIds);

  const value: TransferState = useMemo(
    () => ({
      associateFilters: associateFilters || [],
      isLoading: filtersLoading || associatesLoading,
      filteredAssociates: filteredAssociates || [],
      error:
        filtersError || associatesError ? 'Failed to fetch associates' : '',
      setSelectedDepartmentIds,
      selectedDepartmentIds,
    }),
    [
      associateFilters,
      associatesError,
      associatesLoading,
      filteredAssociates,
      filtersError,
      filtersLoading,
      selectedDepartmentIds,
    ],
  );

  return (
    <TransferContext.Provider value={value}>
      {props.children}
    </TransferContext.Provider>
  );
};

export { TransferProvider, TransferContext };
